import React, { useState, useEffect } from 'react';
import { Table, Segment, Header, Loader, Message, Label } from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';

const MonarchAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/monarch/accounts/');
      setAccounts(response.data.accounts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getAccountTypeColor = (type) => {
    const typeMap = {
      'checking': 'blue',
      'savings': 'green',
      'credit': 'orange',
      'loan': 'red',
      'investment': 'purple',
      'mortgage': 'red',
      'other': 'grey'
    };
    return typeMap[type.toLowerCase()] || 'grey';
  };

  if (loading) {
    return (
      <Segment style={{ minHeight: '200px' }}>
        <Loader active>Loading accounts...</Loader>
      </Segment>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Error</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  // Group accounts by institution
  const accountsByInstitution = accounts.reduce((acc, account) => {
    const institution = account.institution || 'Other';
    if (!acc[institution]) {
      acc[institution] = [];
    }
    acc[institution].push(account);
    return acc;
  }, {});

  return (
    <div>
      <Header as="h2">All Accounts</Header>
      {Object.entries(accountsByInstitution).map(([institution, institutionAccounts]) => (
        <Segment key={institution}>
          <Header as="h3">{institution}</Header>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Account Name</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Balance</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {institutionAccounts.map((account) => (
                <Table.Row key={account.id}>
                  <Table.Cell>{account.name}</Table.Cell>
                  <Table.Cell>
                    <Label color={getAccountTypeColor(account.type)} size="small">
                      {account.type}
                    </Label>
                    {account.subtype && account.subtype !== account.type && (
                      <Label basic size="small" style={{ marginLeft: '5px' }}>
                        {account.subtype}
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <span style={{ 
                      color: account.balance >= 0 ? 'green' : 'red',
                      fontWeight: 'bold' 
                    }}>
                      {formatCurrency(account.balance)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {account.isHidden && <Label size="small">Hidden</Label>}
                    {!account.includeInNetWorth && (
                      <Label size="small" basic>Excluded from Net Worth</Label>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="2">
                  <strong>Total</strong>
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                  <strong>
                    {formatCurrency(
                      institutionAccounts.reduce((sum, acc) => sum + acc.balance, 0)
                    )}
                  </strong>
                </Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Footer>
          </Table>
        </Segment>
      ))}
    </div>
  );
};

export default MonarchAccounts;